import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Check, Search, ChevronRight, ChevronDown, Menu, X, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalHandbook = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [isRead, setIsRead] = useState(false);
  const [readAt, setReadAt] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const sectionRefs = useRef({});
  const contentRef = useRef(null);

  useEffect(() => {
    loadHandbook();
  }, []);

  const loadHandbook = async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/handbook-full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSections(res.data.sections || []);
      setIsRead(res.data.is_read || false);
      setReadAt(res.data.read_at);
      if (res.data.sections?.length > 0) {
        setActiveSection(res.data.sections[0].id);
      }
      // Auto-mark as read when employee opens the handbook
      if (!res.data.is_read) {
        markAsRead();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await axios.post(`${API}/api/employee-portal/handbook-full/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsRead(true);
      setReadAt(new Date().toISOString());
    } catch (err) { console.error(err); }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setTocOpen(false);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Track active section on scroll
  useEffect(() => {
    const container = contentRef.current;
    if (!container || sections.length === 0) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop + 100;
      let current = sections[0]?.id;
      for (const s of sections) {
        const el = sectionRefs.current[s.id];
        if (el && el.offsetTop <= scrollTop) {
          current = s.id;
        }
      }
      if (current !== activeSection) {
        setActiveSection(current);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [sections, activeSection]);

  const filtered = search
    ? sections.filter(s => s.title.toLowerCase().includes(search.toLowerCase()))
    : sections;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center" data-testid="portal-handbook-empty">
        <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-200" />
        <h2 className="text-lg font-semibold text-gray-600 mb-2">No Handbook Available</h2>
        <p className="text-sm text-gray-400">The employee handbook has not been published yet.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="portal-handbook-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 px-1">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <BookOpen className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Employee Handbook</h2>
          <p className="text-xs text-gray-400">
            {sections.length} sections
            {isRead && (
              <span className="ml-2 text-green-600">
                <Check className="inline h-3 w-3" /> Read
              </span>
            )}
          </p>
        </div>
        {/* Mobile TOC toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => { window.location.href = `${API}/api/handbook-full/download-pdf`; }}
          data-testid="portal-download-pdf-btn"
        >
          <Download className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">PDF</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="lg:hidden"
          onClick={() => setTocOpen(!tocOpen)}
          data-testid="toc-toggle"
        >
          {tocOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex gap-4 relative">
        {/* TOC Sidebar - sticky on desktop, overlay on mobile */}
        <div className={`
          ${tocOpen ? 'fixed inset-0 z-50 bg-black/30 lg:static lg:bg-transparent' : 'hidden lg:block'}
        `} onClick={() => setTocOpen(false)}>
          <div
            className={`
              w-64 bg-white rounded-xl border shadow-sm flex-shrink-0 overflow-hidden flex flex-col
              lg:sticky lg:top-4 lg:max-h-[calc(100vh-140px)]
              ${tocOpen ? 'fixed right-0 top-0 h-full z-50 rounded-none shadow-xl max-h-screen' : ''}
            `}
            onClick={e => e.stopPropagation()}
          >
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-300" />
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  data-testid="portal-handbook-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {filtered.map(s => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-1.5 transition-colors hover:bg-indigo-50 border-l-2 ${
                    activeSection === s.id
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-medium'
                      : 'border-transparent text-gray-600'
                  }`}
                  data-testid={`portal-toc-${s.section_number}`}
                >
                  <span className="text-gray-400 w-4 flex-shrink-0 text-right">{s.section_number}.</span>
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className="flex-1 bg-white rounded-xl border shadow-sm overflow-y-auto p-5 space-y-6"
          style={{ maxHeight: 'calc(100vh - 140px)' }}
        >
          {filtered.map(section => (
            <div
              key={section.id}
              ref={el => sectionRefs.current[section.id] = el}
              className="scroll-mt-4"
              data-testid={`portal-section-${section.section_number}`}
            >
              <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span className="text-indigo-500 text-sm font-normal">{section.section_number}.</span>
                {section.title}
              </h3>
              <div
                className="text-sm text-gray-700 leading-relaxed
                  [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5
                  [&_li]:mb-1 [&_strong]:font-semibold [&_em]:italic
                  [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1
                  [&_.handbook-table]:w-full [&_.handbook-table]:border-collapse [&_.handbook-table]:mt-2 [&_.handbook-table]:text-xs
                  [&_.handbook-table_th]:border [&_.handbook-table_th]:px-2 [&_.handbook-table_th]:py-1.5 [&_.handbook-table_th]:bg-indigo-50 [&_.handbook-table_th]:text-left [&_.handbook-table_th]:font-semibold
                  [&_.handbook-table_td]:border [&_.handbook-table_td]:px-2 [&_.handbook-table_td]:py-1.5"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
              <hr className="mt-5 border-gray-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortalHandbook;
